import dayjs from 'dayjs';

import { DATE_TIME_WITH_OFFSET } from '@cast/constants';
import {
  Column,
  EllipsisWithTooltip,
  TableHeader,
  Typography,
  Box,
} from '@cast/design-system';
import { OptimizationWorkloadPod } from '@cast/types';

import { makeMultiSorter } from 'components/table';

import { RecommendationCell } from './cells/RecommendationCell';
import { StatusCell } from './cells/StatusCell';
import {
  POD_APPLIED_STATUS_RANK,
  getPodAppliedStatus,
  getPodRecommendedRequests,
} from '../../_podStatusUtils';

import {
  NoData,
  NoResults,
  SearchableTable,
  SearchableTableRecordsCount,
} from '@cast/shared-search-ui';

const RecommendationSorter = makeMultiSorter({
  sortBy: [
    {
      name: 'CPU',
      accessor: ({ containers }) =>
        getPodRecommendedRequests(containers)?.cpuCores ?? 0,
      testId: 'cpu',
    },
    {
      name: 'GiB',
      accessor: ({ containers }) =>
        getPodRecommendedRequests(containers)?.memoryGib ?? 0,
      testId: 'gib',
    },
  ],
});

type AppliedPodsTableProps = {
  // True when BE returns recommendationStatus.type === STATUS_UNKNOWN for the
  // parent workload (vertical autoscaling disabled). When true, every row's
  // status column renders as notApplied regardless of per-pod state.
  isOptimizationDisabled?: boolean;
};

export const AppliedPodsTable = ({
  isOptimizationDisabled,
}: AppliedPodsTableProps = {}) => {
  return (
    <Box mx={-32} mt={24}>
      <SearchableTable
        testId="applied-pods-table"
        components={{
          noData: <NoData entity="pods" />,
          noResults: <NoResults entity="pods" />,
        }}
        outerHeader={
          <TableHeader
            recordsCount={
              <SearchableTableRecordsCount
                title="pods"
                sx={{ paddingLeft: 32 }}
              />
            }
          />
        }
      >
        <Column<OptimizationWorkloadPod>
          id="name"
          header="pod"
          sortable
          minWidth={150}
          cellWrapperSx={{ paddingLeft: 32 }}
          renderCell={({ name }) => (
            <EllipsisWithTooltip sx={{ fontWeight: 500 }}>
              {name}
            </EllipsisWithTooltip>
          )}
        />

        <Column<OptimizationWorkloadPod>
          id="recommendation"
          header="recommendation"
          sortable
          width={185}
          Sorter={RecommendationSorter}
          renderCell={({ containers }) => (
            <RecommendationCell containers={containers} />
          )}
        />

        <Column<OptimizationWorkloadPod>
          id="status"
          header="status"
          width={110}
          sortable
          sortingType="number"
          accessor={({ isLatestRecommendation, recommendation }) =>
            POD_APPLIED_STATUS_RANK[
              getPodAppliedStatus(
                {
                  isLatestRecommendation,
                  recommendation,
                },
                isOptimizationDisabled
              )
            ]
          }
          renderCell={({ isLatestRecommendation, recommendation }) => (
            <StatusCell
              isLatestRecommendation={isLatestRecommendation}
              recommendation={recommendation}
              isOptimizationDisabled={isOptimizationDisabled}
            />
          )}
        />

        <Column<OptimizationWorkloadPod>
          id="appliedAt"
          header="timestamp"
          width={240}
          sortable
          cellWrapperSx={{ paddingRight: 32 }}
          accessor={({ recommendation }) => recommendation?.appliedAt}
          renderCell={({ recommendation }) => (
            <Typography variant="P12R">
              {recommendation?.appliedAt
                ? dayjs(recommendation?.appliedAt).format(DATE_TIME_WITH_OFFSET)
                : ''}
            </Typography>
          )}
        />
      </SearchableTable>
    </Box>
  );
};
